'use client';

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { UserProfile } from '@/types/catalog';

interface AuthState {
  loggedIn: boolean;
  /** True while the session cookie is being validated on first load. */
  hydrating: boolean;
  user: UserProfile | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (name: string, email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

async function fetchMe(): Promise<UserProfile | null> {
  try {
    const res = await fetch('/api/backend/me', { headers: { accept: 'application/json' } });
    if (!res.ok) return null;
    const d = await res.json();
    return { id: d.id, name: d.name, email: d.email, isAdmin: !!d.is_admin };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [hydrating, setHydrating] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchMe().then((u) => {
      if (!cancelled) {
        setUser(u);
        setHydrating(false);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error('invalid_credentials');
    const d = await res.json();
    setUser({ id: d.user.id, name: d.user.name, email: d.user.email, isAdmin: !!d.user.is_admin });
  }, []);

  const signUp = useCallback(async (name: string, email: string, password: string) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d?.error === 'email_taken' ? 'email_taken' : 'register_failed');
    }
    const d = await res.json();
    setUser({ id: d.user.id, name: d.user.name, email: d.user.email, isAdmin: !!d.user.is_admin });
  }, []);

  const signOut = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setUser(null);
    // Hard-navigate home so every logged-in view resets and the (httpOnly)
    // session cookie is definitively gone from the next request.
    if (typeof window !== 'undefined') window.location.assign('/');
  }, []);

  return (
    <AuthContext.Provider value={{ loggedIn: !!user, hydrating, user, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
